

import React, { useState, useEffect, useRef } from 'react';
import Button from './Button';
import StepIndicator from './StepIndicator';
import { Essay } from '../src/types';

interface ModelProgress {
  modelId: string;
  modelName: string;
  status: 'pending' | 'marking' | 'completed' | 'failed' | 'retrying';
  detail: string;
  retryCount: number;
}

interface Task {
  essay: Essay;
  status: 'pending' | 'marking' | 'completed' | 'failed' | 'cancelled';
  detail: string;
  modelProgress: ModelProgress[];
  overallProgress: {
    completed: number;
    total: number;
  };
}

interface BatchRemarkProgressModalProps {
  isOpen: boolean;
  essaysToMark: Essay[];
  onRemarkEssay: (essay: Essay, onStatusUpdate?: (message: string) => void, selectedModelIds?: string[]) => Promise<boolean>;
  selectedModelIds?: string[];
  onClose: () => void;
  showModelNames?: boolean;
}

const StatusIcon = ({ status }: { status: Task['status'] }) => {
  const baseClass = "h-5 w-5 mr-3 flex-shrink-0";
  switch (status) {
    case 'completed':
      return (
        <svg className={`${baseClass} text-green-500`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      );
    case 'failed':
      return (
        <svg className={`${baseClass} text-red-500`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    case 'cancelled':
      return (
        <svg className={`${baseClass} text-secondary-500`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
      );
    case 'marking':
       return (
         <svg className={`${baseClass} animate-spin text-primary-500`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
         </svg>
       );
    case 'pending':
    default:
      return (
        <svg className={`${baseClass} text-secondary-400`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

const ModelProgressIndicator = ({ modelProgress, showModelNames = true }: { modelProgress: ModelProgress; showModelNames?: boolean }) => {
  const getModelDisplayName = (modelId: string) => {
    if (!showModelNames) return 'AI model';
    // Extract short names for display
    if (modelId.includes('gemini')) return 'Gemini';
    if (modelId.includes('gpt') || modelId.includes('openai')) return 'GPT';
    if (modelId.includes('claude')) return 'Claude';
    return modelId.substring(0, 6); // Fallback to first 6 characters
  };

  const getStatusColor = () => {
    switch (modelProgress.status) {
      case 'completed': return 'bg-green-500 border-green-600';
      case 'failed': return 'bg-red-500 border-red-600';
      case 'retrying': return 'bg-yellow-500 border-yellow-600';
      case 'marking': return 'bg-blue-500 border-blue-600 animate-pulse';
      case 'pending': 
      default: return 'bg-gray-300 border-gray-400';
    }
  };

  const getStatusIcon = () => {
    switch (modelProgress.status) {
      case 'completed':
        return (
          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'failed':
        return (
          <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'retrying':
        return (
          <svg className="h-3 w-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      case 'marking':
        return (
          <svg className="h-3 w-3 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div 
      className={`w-6 h-6 rounded border-2 flex items-center justify-center ${getStatusColor()}`}
      title={`${getModelDisplayName(modelProgress.modelId)}: ${modelProgress.status}${modelProgress.retryCount > 0 ? ` (retry ${modelProgress.retryCount})` : ''}${modelProgress.detail ? ` - ${modelProgress.detail}` : ''}`}
    >
      {getStatusIcon()}
    </div>
  );
};

const ModelProgressRow = ({ modelProgress, showModelNames = true }: { modelProgress: ModelProgress[]; showModelNames?: boolean }) => {
  if (modelProgress.length === 0) return null;
  
  return (
    <div className="flex gap-1 mt-1">
      {modelProgress.map(progress => (
        <ModelProgressIndicator key={progress.modelId} modelProgress={progress} showModelNames={showModelNames} />
      ))}
    </div>
  );
};

const BatchRemarkProgressModal = ({ isOpen, essaysToMark, onRemarkEssay, selectedModelIds = [], onClose, showModelNames = false }: BatchRemarkProgressModalProps) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentStatusMessage, setCurrentStatusMessage] = useState('Initializing...');
  const stopFlag = useRef(false);
  const processStartedRef = useRef(false); // Lock to prevent multiple process starts
  const [isStopping, setIsStopping] = useState(false);
  const [isFinished, setIsFinished] = useState(false);

  useEffect(() => {
    // Only start the batch process if the modal is open AND it hasn't already been started.
    if (isOpen && !processStartedRef.current) {
      processStartedRef.current = true; // Set the lock
      stopFlag.current = false;
      setIsStopping(false);
      setIsFinished(false);
      
      const initialTasks: Task[] = essaysToMark.map(essay => ({ 
        essay, 
        status: 'pending' as const, 
        detail: '',
        modelProgress: selectedModelIds.map(modelId => ({
          modelId,
          modelName: modelId, // This will be updated with proper model names
          status: 'pending' as const,
          detail: '',
          retryCount: 0
        })),
        overallProgress: {
          completed: 0,
          total: selectedModelIds.length
        }
      }));
      setTasks(initialTasks);

      const runBatch = async () => {
        setCurrentStatusMessage(`Starting batch remark of ${essaysToMark.length} essays...`);
        
        for (let i = 0; i < essaysToMark.length; i++) {
          if (stopFlag.current) {
             // Mark all remaining 'pending' tasks as 'cancelled'
            setTasks(prev => {
                const newTasks = [...prev];
                for (let j = i; j < newTasks.length; j++) {
                    if(newTasks[j].status === 'pending') {
                       newTasks[j] = { ...newTasks[j], status: 'cancelled', detail: 'Skipped by user.' };
                    }
                }
                return newTasks;
            });

            setCurrentStatusMessage('Batch process stopped by user.');
            setIsFinished(true);
            return;
          }
          
          const essay = essaysToMark[i];
          
          const onStatusUpdate = (message: string) => {
              setTasks(prev => prev.map(task => 
                  task.essay.id === essay.id ? { ...task, status: 'marking', detail: message } : task
              ));
          };

          onStatusUpdate('Preparing to mark...');
          setCurrentStatusMessage(`Marking essay for ${essay.studentName}...`);
          
          try {
            const success = await onRemarkEssay(essay, onStatusUpdate, selectedModelIds);
            console.log(`Batch remarking result for essay ${essay.id} (${essay.studentName}):`, success);
            setTasks(prev => prev.map(task => 
              task.essay.id === essay.id ? { ...task, status: success ? 'completed' : 'failed', detail: success ? 'Done.' : 'An error occurred.' } : task
            ));
          } catch (e) {
            console.error(`Error re-marking essay ${essay.id}:`, e);
            const errorMessage = e instanceof Error ? e.message : 'An unknown error occurred.';
            setTasks(prev => prev.map(task => 
              task.essay.id === essay.id ? { ...task, status: 'failed', detail: errorMessage } : task
            ));
          }
        }
        
        setCurrentStatusMessage('Batch process completed. Refreshing essay list...');
        
        // Add a small delay to ensure all database writes have completed
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        setIsFinished(true);
      };

      runBatch();

    } else if (!isOpen) {
        processStartedRef.current = false; // Reset the lock when the modal closes
    }
  }, [isOpen, essaysToMark, onRemarkEssay]);

  const handleStop = () => {
    setIsStopping(true);
    stopFlag.current = true;
    setCurrentStatusMessage('Stopping process after current essay...');
  };

  if (!isOpen) {
    return null;
  }
  
  const completedTasks = tasks.filter(t => ['completed', 'failed', 'cancelled'].includes(t.status));
  const pendingTasks = tasks.filter(t => t.status === 'pending' || t.status === 'marking');
  const batchSteps = [
    { id: 'queue', name: 'Queued' },
    { id: 'marking', name: 'Marking' },
    { id: 'finalising', name: 'Finalising' },
    { id: 'done', name: 'Done' },
  ];
  const currentBatchStep = (() => {
    if (isFinished) return 'done';
    if (isStopping || (pendingTasks.length === 0 && tasks.length > 0)) return 'finalising';
    if (completedTasks.length > 0 || tasks.some(task => task.status === 'marking')) return 'marking';
    return 'queue';
  })();

  return (
    <div className="fixed inset-0 bg-secondary-900 bg-opacity-90 z-50 flex flex-col p-4 sm:p-8">
      <div className="w-full max-w-4xl mx-auto flex-grow flex flex-col bg-white rounded-lg shadow-2xl overflow-hidden">
        
        <div className="p-6 border-b border-secondary-200">
          <StepIndicator steps={batchSteps} currentStep={currentBatchStep} compact className="mb-4" />
          <h2 className="text-2xl font-bold text-primary-700">Batch Re-marking in Progress</h2>
          <p className="text-sm text-secondary-600 mt-1">{currentStatusMessage}</p>
          <div className="w-full bg-secondary-200 rounded-full h-2.5 mt-4">
            <div 
              className="bg-primary-600 h-2.5 rounded-full transition-all duration-500" 
              style={{ width: `${(completedTasks.length / tasks.length) * 100}%` }}>
            </div>
          </div>
          <p className="text-center text-sm font-medium text-secondary-700 mt-2">
            {completedTasks.length} of {tasks.length} essays completed
          </p>
        </div>
        
        <div className="flex-grow p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Pending List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-secondary-800 border-b pb-2">Queue ({pendingTasks.length})</h3>
            <ul className="space-y-2">
              {pendingTasks.map((task) => (
                <li key={task.essay.id} className={`flex items-start p-3 rounded-md text-sm ${task.status === 'marking' ? 'bg-primary-100 ring-2 ring-primary-400' : 'bg-secondary-50'}`}>
                  <StatusIcon status={task.status} />
                  <div className="flex-grow">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-secondary-800">{task.essay.studentName}</span>
                      <span className="text-xs text-secondary-500">
                        {task.overallProgress.completed}/{task.overallProgress.total}
                      </span>
                    </div>
                    {task.status === 'marking' && task.detail && (
                      <p className="text-xs text-primary-700 italic mt-1">{task.detail}</p>
                    )}
                    <ModelProgressRow modelProgress={task.modelProgress} showModelNames={showModelNames} />
                  </div>
                </li>
              ))}
              {pendingTasks.length === 0 && !isFinished && <p className="text-sm text-secondary-500 italic">Processing queue...</p>}
               {pendingTasks.length === 0 && isFinished && <p className="text-sm text-secondary-500 italic">Queue is empty.</p>}
            </ul>
          </div>
          
          {/* Completed List */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-secondary-800 border-b pb-2">Completed ({completedTasks.length})</h3>
            <ul className="space-y-2">
              {completedTasks.map(({ essay, status, detail }) => (
                <li key={essay.id} className="flex items-center p-3 rounded-md bg-secondary-100 text-sm">
                  <StatusIcon status={status} />
                   <div className="flex-grow">
                      <span className="font-medium text-secondary-800">{essay.studentName}</span>
                      {status === 'failed' && detail && (
                        <p className="text-xs text-red-700 italic" title={detail}>Error: {detail.substring(0, 50)}...</p>
                      )}
                      {status === 'cancelled' && detail && (
                        <p className="text-xs text-secondary-600 italic">{detail}</p>
                      )}
                  </div>
                </li>
              ))}
               {completedTasks.length === 0 && <p className="text-sm text-secondary-500 italic">No essays completed yet.</p>}
            </ul>
          </div>
        </div>

        <div className="p-6 border-t border-secondary-200 flex justify-center">
            {isFinished ? (
                 <Button onClick={onClose} size="lg" variant="primary">
                    Close
                 </Button>
            ) : (
                <Button onClick={handleStop} size="lg" variant="danger" isLoading={isStopping} loadingText="Stopping...">
                    Stop Process
                </Button>
            )}
        </div>
      </div>
    </div>
  );
};

export default BatchRemarkProgressModal;
